import { FC, useState } from 'react';
import { ChevronDown, ChevronUp, Search, Mail } from 'lucide-react';

interface FaqItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ_ITEMS: FaqItem[] = [
  // Général
  {
    category: 'Général',
    question: "Qu'est-ce qu'ImmoHelvet ?",
    answer: "ImmoHelvet est une plateforme suisse d'aide à l'investissement immobilier. Elle agrège automatiquement les annonces de plusieurs portails immobiliers suisses, les analyse avec des indicateurs financiers (rendement brut, cash-flow, ROE) et un score d'opportunité basé sur votre profil investisseur. Elle intègre aussi des données officielles suisses (OFS, RegBL, communes) pour contextualiser chaque bien.",
  },
  {
    category: 'Général',
    question: "À qui s'adresse ImmoHelvet ?",
    answer: "ImmoHelvet est destinée aux investisseurs immobiliers suisses — qu'ils soient débutants ou expérimentés — qui cherchent à gagner du temps dans leur recherche de biens, à objectiver leurs décisions avec des données financières, et à gérer leur portefeuille d'actifs achetés.",
  },
  {
    category: 'Général',
    question: "Quelle est la différence avec les portails comme ImmoScout24 ou homegate.ch ?",
    answer: "ImmoHelvet n'est pas un portail d'annonces — c'est un outil d'analyse. Nous agrégeons les annonces de plusieurs portails et ajoutons une couche analytique : score d'opportunité, estimation de loyer, analyse PPE par IA, données socio-démographiques de la commune, carte interactive, comparateur de scénarios d'acquisition. L'objectif est de vous aider à décider, pas seulement à chercher.",
  },
  // Fonctionnement
  {
    category: 'Fonctionnement',
    question: "Comment les annonces sont-elles importées ?",
    answer: "Un système de scraping automatique (harvest) tourne chaque nuit et importe les nouvelles annonces depuis plusieurs sources suisses (cirgroup.ch, mulleretchriste.ch, acheter-louer.ch, immobilier.ch, etc.). Chaque annonce est automatiquement enrichie : estimation du loyer, calcul du rendement brut, score d'opportunité selon votre profil, analyse de la description par IA.",
  },
  {
    category: 'Fonctionnement',
    question: "Comment fonctionne le score d'opportunité ?",
    answer: "Le score (0–100) est calculé selon 4 dimensions pondérées : financière (40%) — rendement, ROE estimé, cash-flow par rapport à vos cibles ; bien (30%) — surface, pièces, localisation ; stratégie (20%) — compatibilité avec votre stratégie (locatif, flip, mixte) ; comportementale (10%) — ancienneté de l'annonce, complétude de l'adresse. Un score ≥ 85 est classé Opportunité, ≥ 70 Intéressant.",
  },
  {
    category: 'Fonctionnement',
    question: "Qu'est-ce que l'analyse PPE par IA ?",
    answer: "Sur le plan PRO, vous pouvez uploader les documents PPE (Plan Financier, PV d'AG, Règlement, états des charges). Notre pipeline IA (OCR + RAG) analyse ces documents et produit un rapport structuré sur 5 familles : financier, technique, énergie, gouvernance, risques. Un score global PPE de 0 à 10 vous aide à évaluer la qualité de la copropriété.",
  },
  {
    category: 'Fonctionnement',
    question: "Puis-je importer un bien que j'ai trouvé moi-même ?",
    answer: "Oui. Dans la section \"Mes propriétés\", vous pouvez coller l'URL d'une annonce ou créer manuellement un bien. Ces biens sont privés — ils ne sont visibles que par vous.",
  },
  // Abonnements & tarifs
  {
    category: 'Abonnements',
    question: "Quels sont les plans disponibles ?",
    answer: "ImmoHelvet propose 3 plans : FREEMIUM (limité à 10 biens privés, lecture du catalogue partagé), PREMIUM (import illimité, catalogue complet, alertes email), PRO (tout Premium + analyse PPE par IA, dossier banquier PDF). Les nouveaux comptes bénéficient d'une période d'essai PREMIUM de 30 jours.",
  },
  {
    category: 'Abonnements',
    question: "Y a-t-il une période d'essai ?",
    answer: "Oui. Tout nouveau compte bénéficie automatiquement d'un accès PREMIUM gratuit pendant 30 jours, sans carte bancaire requise. Vous pouvez tester toutes les fonctionnalités Premium avant de choisir.",
  },
  {
    category: 'Abonnements',
    question: "Comment fonctionnent les alertes par email ?",
    answer: "Dans votre profil investisseur (étape Alertes), vous choisissez la fréquence (immédiat, quotidien, hebdomadaire), le score minimum (50–95) et le type de biens à inclure. Dès qu'une nouvelle annonce importée correspond à votre profil, vous recevez un email avec la fiche du bien et un lien direct vers l'analyse. Les notifications push (mobile) sont aussi disponibles sur Android Chrome et iOS 16.4+.",
  },
  // Données & fiabilité
  {
    category: 'Données & fiabilité',
    question: "D'où proviennent les données de loyer estimé ?",
    answer: "L'estimation de loyer est calculée par notre service d'estimation basé sur les biens comparables en base de données (mêmes critères : commune, surface, pièces). C'est une estimation statistique — elle est affichée avec un indicateur de confiance (faible / moyen / élevé). Elle n'a pas valeur d'expertise.",
  },
  {
    category: 'Données & fiabilité',
    question: "Que signifie le badge \"Retiré du marché\" ?",
    answer: "Chaque nuit, notre système vérifie automatiquement si les annonces actives sont toujours disponibles. Quand l'annonce originale n'est plus accessible (HTTP 404 ou mention explicite \"vendu\"), elle est marquée \"Retiré du marché\" avec la date. Ces biens restent consultables à titre d'historique.",
  },
  {
    category: 'Données & fiabilité',
    question: "Les données RegBL/GWR sont-elles fiables ?",
    answer: "Le module RegBL interroge le Registre fédéral des bâtiments et des logements (GWR) de la Confédération via l'API officielle. Les données (année de construction, nombre de logements, catégorie d'énergie) sont celles enregistrées par les communes — elles peuvent présenter des décalages avec la réalité si la commune n'a pas mis à jour ses données.",
  },
  // Compte & confidentialité
  {
    category: 'Compte & confidentialité',
    question: "Mes données sont-elles partagées avec des tiers ?",
    answer: "Non. Vos données personnelles, sélections et analyses restent strictement privées. Le catalogue d'annonces est partagé entre utilisateurs (biens publics importés automatiquement), mais vos annotations, scores, sélections et documents PPE sont isolés par compte.",
  },
  {
    category: 'Compte & confidentialité',
    question: "L'application est-elle hébergée en Suisse ?",
    answer: "L'application est hébergée sur un VPS Infomaniak (Genève, Suisse). Infomaniak est une entreprise suisse certifiée ISO 27001 et respectant le RGPD.",
  },
  {
    category: 'Compte & confidentialité',
    question: "Comment réinitialiser mon mot de passe ?",
    answer: "Sur la page de connexion, cliquez sur « Mot de passe oublié ? ». Entrez votre email — vous recevrez un lien de réinitialisation valable 1 heure. Si vous ne recevez pas l'email, vérifiez vos spams.",
  },
];

const CATEGORIES = [...new Set(FAQ_ITEMS.map(f => f.category))];

const AccordionItem: FC<{ item: FaqItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        aria-expanded={open}
      >
        <span className="font-medium text-gray-900 dark:text-gray-100 text-sm leading-snug pr-4">
          {item.question}
        </span>
        {open
          ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );
};

export const FaqPage: FC = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = FAQ_ITEMS.filter(item => {
    const matchesSearch = !search
      || item.question.toLowerCase().includes(search.toLowerCase())
      || item.answer.toLowerCase().includes(search.toLowerCase());
    const matchesCat = !activeCategory || item.category === activeCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 text-white py-16 px-4 text-center">
        <h1 className="text-3xl font-bold mb-3">Foire aux questions</h1>
        <p className="text-blue-200 max-w-lg mx-auto text-sm">
          Toutes les réponses sur ImmoHelvet — comment ça fonctionne, pour qui, et combien ça coûte.
        </p>
        {/* Barre de recherche */}
        <div className="mt-8 max-w-md mx-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une question…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Filtres catégories */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border
              ${!activeCategory
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}
          >
            Toutes
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(c => c === cat ? null : cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border
                ${activeCategory === cat
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Questions */}
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>Aucune question ne correspond à votre recherche.</p>
            <button onClick={() => { setSearch(''); setActiveCategory(null); }} className="mt-3 text-blue-600 text-sm underline">
              Voir toutes les questions
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item, i) => <AccordionItem key={i} item={item} />)}
          </div>
        )}

        {/* CTA contact */}
        <div className="mt-16 text-center p-8 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">Vous n'avez pas trouvé votre réponse ?</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Notre équipe vous répond sous 24h.</p>
          <a
            href="mailto:contact@immo.watts-drive.ch"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-colors"
          >
            <Mail className="w-4 h-4" />
            Contacter le support
          </a>
          <p className="mt-4 text-xs text-gray-400">
            Ou revenez sur{' '}
            <a href="/login" className="text-blue-600 hover:underline">votre espace</a>
            {' '}pour accéder à l'aide contextuelle.
          </p>
        </div>
      </div>
    </div>
  );
};
